import { html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Shop } from '../chat/types.js';
import { sanitizeUrl, dispatchChatMarianaLinks } from '../../utils/chat-utils.js';
import styles from '../../styles/mariana-shop-cards.css?inline';
import { BaseCardCarousel } from './base-card-carousel.js';
import { getPostalAdminLine, getHoursLines, getDistanceLabel, renderShopStatusTag, isSpecialScheduleForToday, getShopImage } from './shop-detail-utils.js';
import './mariana-shop-detail-inline.js';

const DESKTOP_BREAKPOINT_PX = 768;

/**
 * Shop Cards Component with Horizontal Scroll
 * Displays a list of shops (name, address, hours, distance) in horizontally scrollable cards
 */
@customElement('mariana-shop-cards')
export class MarianaShopCards extends BaseCardCarousel<Shop> {
  static readonly styles = unsafeCSS(styles);

  private get isActionLocked(): boolean {
    return this.hasAttribute('data-action-pending') || this.hasAttribute('data-action-locked');
  }

  @property({ type: String })
  source = '';

  @state() private desktopDetailShop: Shop | null = null;

  private readonly mobileMediaQuery = globalThis.matchMedia(`(max-width: ${DESKTOP_BREAKPOINT_PX}px)`);

  connectedCallback(): void {
    super.connectedCallback();
    this.mobileMediaQuery.addEventListener('change', this.handleMobileMediaChange);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.mobileMediaQuery.removeEventListener('change', this.handleMobileMediaChange);
  }

  /**
   * If the viewport crosses into mobile width while the desktop inline detail
   * is open, hand off to the mobile modal sheet instead of leaving the inline
   * view (with its scroll-tied sticky header/footer) up on a small screen.
   */
  private readonly handleMobileMediaChange = (e: MediaQueryListEvent): void => {
    if (!e.matches || !this.desktopDetailShop) return;

    const shop = this.desktopDetailShop;
    this.desktopDetailShop = null;
    this.dispatchEvent(new CustomEvent('shop-more-info', {
      detail: { shop },
      bubbles: true,
      composed: true,
    }));
  };

  private get isAppCC(): boolean {
    return this.source.toLowerCase() === 'appcc';
  }

  /** Called by mariana-chat when the mobile modal hands off back to this card on a desktop-width resize. */
  public openInlineDetail(shop: Shop): void {
    this.desktopDetailShop = shop;
  }

  @property({ type: Array })
  get shops(): Shop[] {
    return this.cards;
  }

  set shops(value: Shop[]) {
    this.cards = value;
  }

  protected getContainerClass(): string {
    return 'shops-scroll-container';
  }

  protected getCardClass(): string {
    return 'shop-card';
  }

  private handleImageClick(shop: Shop) {
    if (this.isActionLocked) return;

    const pageUrl = shop.page_url;
    if (!pageUrl) return;

    if (this.isAppCC) {
      dispatchChatMarianaLinks(pageUrl);
      return;
    }

    window.open(pageUrl, '_blank');
  }

  private handleDirectionsClick(shop: Shop) {
    if (this.isActionLocked) return;

    const url = sanitizeUrl(shop.directions_url || '');
    if (!url) return;

    if (this.isAppCC) {
      dispatchChatMarianaLinks(url);
      return;
    }

    window.open(url, '_blank');
  }

  private handleMoreInfoClick(shop: Shop) {
    if (this.isActionLocked) return;

    if (globalThis.innerWidth > DESKTOP_BREAKPOINT_PX) {
      this.desktopDetailShop = shop;
      return;
    }

    this.dispatchEvent(new CustomEvent('shop-more-info', {
      detail: { shop },
      bubbles: true,
      composed: true,
    }));
  }

  private readonly handleInlineBack = (): void => {
    this.desktopDetailShop = null;
  };

  protected updated(changed: Map<string, unknown>): void {
    super.updated?.(changed);

    // Swapping back from the inline detail view tears down and recreates the
    // carousel's scroll container — BaseCardCarousel's cached reference to it
    // (set once in firstUpdated) would otherwise keep pointing at the detached
    // old element, silently breaking arrow/dot navigation.
    if (changed.has('desktopDetailShop') && !this.desktopDetailShop) {
      this.setupScrollListener();
    }
  }

  render() {
    if (this.desktopDetailShop) {
      return html`
        <mariana-shop-detail-inline
          .shop=${this.desktopDetailShop}
          .source=${this.source}
          @inline-back=${this.handleInlineBack}
        ></mariana-shop-detail-inline>
      `;
    }

    return super.render();
  }

  protected renderCard(shop: Shop, index: number) {
    const image = getShopImage(shop);
    const hours = getHoursLines(shop);
    const hasSpecialSchedule = isSpecialScheduleForToday(shop);
    const directionsUrl = shop.directions_url;
    const distanceLabel = getDistanceLabel(shop);
    const postalAdminLine = getPostalAdminLine(shop);

    return html`
      <div class="shop-card" data-index="${index}">
        <div class="shop-image-container">
          ${image ? html`
            <img
              src="${image}"
              alt="${shop.name || 'Loja'}"
              class="shop-image"
              @click=${() => this.handleImageClick(shop)}
              style="cursor: pointer;"
            />
          ` : html`
            <div class="shop-image-placeholder" @click=${() => this.handleImageClick(shop)} style="cursor: pointer;">
              <div class="shop-image-placeholder-icon"></div>
            </div>
          `}
          ${distanceLabel ? html`
            <div class="shop-distance-chip">
              <div class="shop-distance-icon"></div>
              <span class="shop-distance-text">${distanceLabel}</span>
            </div>
          ` : ''}
        </div>

        <div class="shop-content">
          <p class="shop-name">${shop.name || 'Sem nome'}</p>
          <p class="shop-address">${shop.street_line_1 || '-'}</p>
          ${postalAdminLine ? html`
            <p class="shop-address-extra">${postalAdminLine}</p>
          ` : ''}

          ${hours.length > 0 ? html`
            <div class="shop-hours-row">
              <div class="shop-hours-icon"></div>
              <div class="shop-hours-lines">
                ${hours.map(line => html`<div class="shop-hours-line ${hasSpecialSchedule ? 'shop-hours-line--special' : ''}">Hoje ${line}</div>`)}
              </div>
              ${renderShopStatusTag(shop, hasSpecialSchedule)}
            </div>
          ` : ''}

          <div class="shop-ctas">
            <button
              class="shop-cta shop-cta--info"
              type="button"
              @click=${() => this.handleMoreInfoClick(shop)}
            >
              <div class="shop-cta-icon shop-cta-info-icon"></div>
              Mais informações
            </button>
            ${directionsUrl ? html`
              <button
                class="shop-cta shop-cta--directions"
                type="button"
                @click=${() => this.handleDirectionsClick(shop)}
              >
                <div class="shop-cta-icon"></div>
                Obter direções
              </button>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mariana-shop-cards': MarianaShopCards;
  }
}
